import { Trans, useTranslation } from '@repo/translations/use-translation';
import { lazy, Suspense } from 'react';

import { useMembershipBenefit } from '../../../../hooks/useMembershipsComparison';

import styles from './ProgramPlanBenefits.module.css';

import type { MembershipsComparison } from '@repo/types/memberships-comparison';

const Check = lazy(() => import('./check.svg'));

export interface ProgramPlanBenefitsProps {
  membershipsComparison: MembershipsComparison;
  plan: 'BASIC' | 'PREMIUM';
}

export const ProgramPlanBenefits = ({ membershipsComparison, plan }: ProgramPlanBenefitsProps) => {
  const { t } = useTranslation();
  const membershipInfo =
    plan === 'BASIC'
      ? membershipsComparison.basicLoyaltyMembershipInfo
      : membershipsComparison.premiumLoyaltyMembershipInfo;

  const { getMembershipBenefitType } = useMembershipBenefit(membershipInfo);
  const points = getMembershipBenefitType('BONUS_POINTS_WELCOME_GIFT')?.amount;

  return (
    <div className={styles.benefitsContainer}>
      {plan === 'BASIC' ? (
        <>
          <span className={styles.title}>
            {t('loyalty-program.public-landing-page.program-plans.benefits-list.basic-title')}
          </span>
          <ul className={styles.list}>
            <li>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                {t(
                  'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-basic-1',
                  {
                    points: points,
                  },
                )}
              </div>
            </li>
            <li>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                {t(
                  'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-basic-2',
                )}
              </div>
            </li>
            <li>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                {t(
                  'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-basic-3',
                )}
              </div>
            </li>
            <li>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                {t(
                  'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-basic-4',
                )}
              </div>
            </li>
          </ul>
        </>
      ) : (
        <>
          <span className={styles.title}>
            {t('loyalty-program.public-landing-page.program-plans.benefits-list.premium-title')}
          </span>
          <ul className={styles.list}>
            <li>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                {t(
                  'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-premium-1',
                )}
              </div>
            </li>
            <li className={styles.premiumBenefit} data-testid={'discount-benefit'}>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                <Trans
                  i18nKey={
                    'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-premium-2'
                  }
                  values={{ discount: getMembershipBenefitType('PERCENTAGE_DISCOUNT')?.discount }}
                  components={{
                    Benefit: <span className={styles.highlight} />,
                  }}
                />
              </div>
            </li>
            <li>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                {t(
                  'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-premium-3',
                )}
              </div>
            </li>
            <li>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                {t(
                  'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-premium-4',
                  {
                    points: points,
                  },
                )}
              </div>
            </li>
            <li>
              <Suspense fallback={null}>
                <Check className={styles.checkIcon} aria-hidden="true" />
              </Suspense>
              <div>
                {t(
                  'loyalty-program.public-landing-page.program-plans.benefits-list.benefit-premium-5',
                )}
              </div>
            </li>
          </ul>
        </>
      )}
    </div>
  );
};
